import React, { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  FaFileAlt,
  FaFileUpload,
  FaCheck,
  FaTimes,
  FaHistory,
  FaPrint,
  FaQuestion,
  FaRegHandPaper,
  FaSyncAlt,
  FaInfoCircle,
  FaEdit,
  FaFileSignature,
} from 'react-icons/fa';
import { Column } from 'react-table';
import TooltipWrapper from 'components/TooltipWrapper';
import ReactTableWrapper from 'components/table/ReactTableWrapper';
import Button from 'components/Button';
import styled from 'styled-components';
import { buildClientUrl } from 'utils/helpers/client';
import { formatDate } from 'utils/helpers/formatters';
import { useActiveClientContext } from 'store/active-client';
import { useWorkModeContext } from 'store/work-mode';
import { useWizardContext } from 'store/wizard';
import { useLanguageContext } from 'store/language';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useModal } from 'store/modal';
import ApproveDoc from 'components/documents/ApproveDoc';
import VerifyDoc from 'components/documents/VerifyDoc';
import ApproveAndSignDoc from 'components/documents/ApproveAndSignDoc';
import VerifyAndSignDoc from 'components/documents/VerifyAndSignDoc';
import SignDoc from 'components/documents/SignDoc';
import RejectDoc from 'components/documents/RejectDoc';
import ReviewDoc from 'components/documents/ReviewDoc';
import SendDoc from 'components/documents/SendDoc';
import DeleteDoc from 'components/documents/DeleteDoc';
import ArchiveDoc from 'components/documents/ArchiveDoc';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import Spinner from 'components/Spinner';
import { IDocumentGroup } from './types';

interface IProps {
  data: any;
  totalCount: number;
  entityName: string;
  docTab?: string;
  isMultiUser?: boolean;
  categoryName: string;
  createdAt: string;
  holderName: string;
  allDocumentButtons: any;
}
const DocsTable: FC<IProps> = ({
  data,
  totalCount,
  entityName,
  docTab,
  isMultiUser,
  categoryName,
  createdAt,
  holderName,
  allDocumentButtons,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { client } = useActiveClientContext();
  const { showModal, hideModal } = useModal();
  const queryClient = useQueryClient();
  const { workMode } = useWorkModeContext();
  const { language } = useLanguageContext();
  const isEmployee = workMode === 'employee';
  const windowInnerWidth = window.innerWidth;
  const isMobile = windowInnerWidth < 768;
  const { setCurrentStep } = useWizardContext();
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);

  // Build columns based on work mode and tab
  const buildColumns = () => {
    const staticColumns: Array<Column<IDocumentGroup>> = [
      {
        Header: 'ID',
        accessor: 'id',
      },
      {
        Header: t('documents.table.docNumber') || '',
        accessor: 'number',
        width: windowInnerWidth > 1600 && windowInnerWidth < 1921 ? '6%' : '8%',
      },
      {
        Header: t('documents.docName') || '',
        accessor: 'name',
        Cell: ({ value, row }) => {
          const { type, id, name_en: nameEn } = row.original;
          return (
            <>
              {type === 'internal' && (
                <TooltipWrapper
                  id="tooltip-internalDoc"
                  overlay={t('documents.internalDoc')}
                >
                  <FaFileAlt size={14} />
                </TooltipWrapper>
              )}
              {type === 'external' && (
                <TooltipWrapper
                  id="tooltip-externalDoc"
                  overlay={t('documents.externalDoc')}
                >
                  <FaFileUpload size={14} />
                </TooltipWrapper>
              )}
              <TooltipWrapper
                id="tooltip-docName"
                overlay={language === 'en' ? value : nameEn || value}
                wrapperClass="u-inline"
              >
                <span
                  className="ml-1 u-underline u-color-inherit u-cursor-pointer"
                  onClick={() => handlePreviewDoc({ showModal, id })}
                >
                  {language === 'en' ? nameEn || value : value}
                </span>
              </TooltipWrapper>
            </>
          );
        },
      },
    ];

    const docNameColumn: Array<Column<IDocumentGroup>> = [
      {
        Header: t('documents.docName') || '',
        accessor: 'name',
        Cell: ({ value, row }) => {
          const { type, id, name_en: nameEn } = row.original;
          return (
            <>
              {type === 'internal' && (
                <TooltipWrapper
                  id="tooltip-internalDoc"
                  overlay={t('documents.internalDoc')}
                >
                  <FaFileAlt size={14} />
                </TooltipWrapper>
              )}
              {type === 'external' && (
                <TooltipWrapper
                  id="tooltip-externalDoc"
                  overlay={t('documents.externalDoc')}
                >
                  <FaFileUpload size={14} />
                </TooltipWrapper>
              )}
              <TooltipWrapper
                id="tooltip-docName"
                overlay={language === 'en' ? value : nameEn || value}
                wrapperClass="u-inline"
              >
                <span
                  className="ml-1 u-underline u-color-inherit u-cursor-pointer"
                  onClick={() => handlePreviewDoc({ showModal, id })}
                >
                  {language === 'en' ? nameEn || value : value}
                </span>
              </TooltipWrapper>
            </>
          );
        },
      },
    ];

    const employerColumns: Array<Column<IDocumentGroup>> = [
      {
        Header: t('documents.table.approvedByEmployer') || '',
        accessor: 'approval',
        Cell: ({ row }) => renderNames(row.original.statuses.employer_approval_status, row.original.rejection_reason),
      },
      {
        Header: t('documents.table.signedByEmployer') || '',
        accessor: 'signing',
        Cell: ({ row }) => renderNames(row.original.statuses.employer_sign_status, row.original.rejection_reason),
      },
    ];

    const employeeColumns: Array<Column<IDocumentGroup>> = [
      {
        Header: t('documents.table.approvedByEmployee') || '',
        accessor: 'approvedByEmployee',
        Cell: ({ row }) => renderNames(row.original.statuses.employee_approval_status, row.original.rejection_reason),
      },
      {
        Header: t('documents.table.signedByEmployee') || '',
        accessor: 'signedByEmployee',
        Cell: ({ row }) => renderNames(row.original.statuses.employee_sign_status, row.original.rejection_reason),
      },
    ];

    const employeeCombinedColumns: Array<Column<IDocumentGroup>> = [
      {
        Header: t('documents.table.approvedSignedByEmployee') || '',
        accessor: 'approval-signing',
        Cell: ({ row }) =>
          renderNames(row.original.statuses.employee_approval_and_sign_status, row.original.rejection_reason),
      },
    ];

    const combinedEmployerColumns: Array<Column<IDocumentGroup>> = [
      {
        Header: t('documents.table.signedAndAprrovedByEmployer') || '',
        accessor: 'employerStatuses',
        Cell: ({ row }) => (
          <>
            <div>{renderNames(row.original.statuses.employer_approval_status, row.original.rejection_reason)}</div>
            <div>{renderNames(row.original.statuses.employer_sign_status, row.original.rejection_reason)}</div>
          </>
        ),
      },
    ];

    const combinedEmployeeColumns: Array<Column<IDocumentGroup>> = [
      {
        Header: t('documents.table.signedAndApprovedByEmployee') || '',
        accessor: 'employeeStatuses',
        Cell: ({ row }) => (
          <>
            <div>{renderNames(row.original.statuses.employee_approval_status, row.original.rejection_reason)}</div>
            <div>{renderNames(row.original.statuses.employee_sign_status, row.original.rejection_reason)}</div>
          </>
        ),
      },
    ];

    const actionsColumn: Array<Column<IDocumentGroup>> = [
      {
        Header: () => <div style={{ textAlign: 'right' }}>{t('table.actions')}</div>,
        accessor: 'actions',
        width: '20%',
        Cell: ({ row }) => {
          const {
            buttons,
            id,
            document_holder_id: docHolderId,
            show_exit_interview_modal: showExitInterview,
            exit_interview_link: exitInterviewUrl,
          } = row.original;
          return (
            <div style={{ textAlign: 'right' }}>
              {renderActions({ buttons, documentId: id, docHolderId, showExitInterview, exitInterviewUrl })}
            </div>
          );
        },
      },
    ];

    if (isEmployee && docTab !== 'received') {
      if (window.innerWidth > 768) {
        return [...staticColumns, ...employeeColumns, ...employerColumns, ...actionsColumn];
      }
      return [...docNameColumn, ...combinedEmployeeColumns, ...combinedEmployerColumns, ...actionsColumn];
    }
    if (!isEmployee && docTab === 'received')
      return [...staticColumns, ...employeeCombinedColumns, ...employerColumns, ...actionsColumn];

    return [...staticColumns, ...employerColumns, ...employeeColumns, ...actionsColumn];
  };

  const renderNames = (value: { name: string; type: string; text: string }[], rejectionReason?: string) => (
    <div>
      {value.map(val => (
        <div
          key={val.name}
          className="d-flex align-items-center mb-2"
        >
          {renderIcon(val.type, val.text, rejectionReason)}
          {val.name && <span className="ml-2">{val.name}</span>}
        </div>
      ))}
    </div>
  );

  const renderIcon = (status: string, text: string, rejectionReason?: string) => (
    <>
      {status === 'completed' && (
        <TooltipWrapper
          id="tooltip-completed"
          overlay={text || t('documents.table.actionComplete')}
        >
          <StatusIconWrapper className="primary">
            <FaCheck
              size={12}
              color="#32bfa3"
            />
          </StatusIconWrapper>
        </TooltipWrapper>
      )}
      {status === 'waiting_action' && (
        <TooltipWrapper
          id="tooltip-waiting"
          overlay={text || t('documents.table.actionWaiting')}
        >
          <StatusIconWrapper className="warning">
            <FaQuestion
              size={12}
              color="ffb822"
            />
          </StatusIconWrapper>
        </TooltipWrapper>
      )}
      {status === 'rejected' && (
        <TooltipWrapper
          id="tooltip-rejected"
          overlay={
            rejectionReason
              ? `${t('documents.table.rejectedWithReason', { reason: rejectionReason })}`
              : text || t('documents.table.actionRejected')
          }
        >
          <StatusIconWrapper className="danger">
            <FaTimes
              size={12}
              color="EF476F"
            />
          </StatusIconWrapper>
        </TooltipWrapper>
      )}
      {status === 'no_action_required' && (
        <TooltipWrapper
          id="tooltip-no-action-required"
          overlay={text || t('documents.table.actionNotRequired')}
        >
          <StatusIconWrapper className="gray">
            <FaRegHandPaper
              size={12}
              color="575962"
            />
          </StatusIconWrapper>
        </TooltipWrapper>
      )}
      {status === 'no_action_required_yet' && (
        <TooltipWrapper
          id="tooltip-no-action-required-yet"
          overlay={text || t('documents.table.actionNotRequiredYet')}
        >
          <StatusIconWrapper className="gray">
            <FaSyncAlt
              size={12}
              color="575962"
            />
          </StatusIconWrapper>
        </TooltipWrapper>
      )}
    </>
  );

  const renderActions = (options: {
    buttons: {
      type: string;
      text: string;
      action_link?: string;
      disabled?: boolean;
      disabled_reason?: string;
    }[];
    documentId: string;
    docHolderId: string;
    hasLockedDocs?: boolean;
    showExitInterview?: boolean;
    exitInterviewUrl?: string;
  }) => {
    const { buttons, documentId, docHolderId, hasLockedDocs, showExitInterview, exitInterviewUrl } = options;
    const sendToEmployeeButton = allDocumentButtons?.find((button: any) => button.type === 'send_to_employee');
    const sendToEmployerButton = allDocumentButtons?.find((button: any) => button.type === 'send_to_employer');
    const buttonText = hasLockedDocs ? t('documents.table.docActionsInProgress') : t('documents.table.send');

    return (
      <>
        {buttons?.map(button => {
          if (button.type === 'sign')
            return (
              <SignDoc
                ariaLabel="Sign document"
                key={button.text}
                id={documentId}
                docHolderId={docHolderId}
                buttonText={button.text}
                handlePreview={docId => handlePreviewDoc({ showModal, id: docId })}
                invalidate={() => queryClient.invalidateQueries(entityName)}
                size={isMobile ? 'lg' : 'sm'}
                showExitInterview={showExitInterview}
                exitInterviewUrl={exitInterviewUrl}
              />
            );

          if (button.type === 'edit' && !isMobile)
            return (
              <>
                <div
                  key={`${docHolderId}-edit`}
                  className="d-inline-block ml-2 mb-2 mb-md-0"
                >
                  <TooltipWrapper
                    id="tooltip-edit"
                    overlay={t('documents.table.editFromFields')}
                  >
                    <Button
                      btnType="primary"
                      size={isMobile ? 'lg' : 'sm'}
                      outlined
                      onClick={() => {
                        // Set current wizard step to 3 before pushing to document wizard view
                        setCurrentStep(3);
                        history.push({
                          pathname: `/documents-wizard/step-3/${docHolderId}`,
                          state: {
                            id: docHolderId,
                          },
                        });
                      }}
                    >
                      <FaEdit size={14} />
                    </Button>
                  </TooltipWrapper>
                </div>
                <div
                  key={`${docHolderId}-redact`}
                  className="d-inline-block ml-2 mb-2 mb-md-0"
                >
                  <TooltipWrapper
                    id="tooltip-redact"
                    overlay={t('documents.table.editFromText')}
                  >
                    <Button
                      btnType="primary"
                      size={isMobile ? 'lg' : 'sm'}
                      outlined
                      onClick={() => {
                        // Set current wizard step to 4 before pushing to document wizard view
                        setCurrentStep(4);
                        history.push({
                          pathname: `/documents-wizard/step-4/${docHolderId}`,
                          state: {
                            id: docHolderId,
                          },
                        });
                      }}
                    >
                      <FaFileSignature size={14} />
                    </Button>
                  </TooltipWrapper>
                </div>
              </>
            );

          if (button.type === 'approve')
            return (
              <ApproveDoc
                ariaLabel="Approve document"
                key={`${docHolderId}-approve`}
                id={documentId}
                docHolderId={docHolderId}
                buttonText={button.text}
                handlePreview={docId => handlePreviewDoc({ showModal, id: docId })}
                invalidate={() => queryClient.invalidateQueries(entityName)}
                size={isMobile ? 'lg' : 'sm'}
              />
            );

          if (button.type === 'verify')
            return (
              <VerifyDoc
                key={`${docHolderId}-verify`}
                id={documentId}
                docHolderId={docHolderId}
                buttonText={button.text}
                handlePreview={docId => handlePreviewDoc({ showModal, id: docId })}
                invalidate={() => queryClient.invalidateQueries(entityName)}
                size={isMobile ? 'lg' : 'sm'}
              />
            );

          if (button.type === 'approve_and_sign')
            return (
              <ApproveAndSignDoc
                ariaLabel="Approve and sign document"
                key={`${docHolderId}-approve_and_sign`}
                id={documentId}
                docHolderId={docHolderId}
                buttonText={button.text}
                handlePreview={docId => handlePreviewDoc({ showModal, id: docId })}
                invalidate={() => queryClient.invalidateQueries(entityName)}
                size={isMobile ? 'lg' : 'sm'}
              />
            );

          if (button.type === 'verify_and_sign')
            return (
              <VerifyAndSignDoc
                key={`${docHolderId}-verify_and_sign`}
                id={documentId}
                docHolderId={docHolderId}
                buttonText={button.text}
                handlePreview={docId => handlePreviewDoc({ showModal, id: docId })}
                invalidate={() => queryClient.invalidateQueries(entityName)}
                size={isMobile ? 'lg' : 'sm'}
              />
            );

          if (button.type === 'reject')
            return (
              <RejectDoc
                ariaLabel="Reject document"
                key={`${docHolderId}-reject`}
                id={documentId}
                docHolderId={docHolderId}
                buttonText={button.text}
                handlePreview={docId => handlePreviewDoc({ showModal, id: docId })}
                invalidate={() => queryClient.invalidateQueries(entityName)}
                size={isMobile ? 'lg' : 'sm'}
              />
            );

          if (button.type === 'review') {
            return (
              <ReviewDoc
                key={`${docHolderId}-review`}
                id={documentId}
                buttonText={
                  button?.disabled ? button?.disabled_reason ?? t('documents.table.actionReview') : button.text
                }
                invalidate={() => queryClient.invalidateQueries(entityName)}
                disabled={!!button?.disabled}
              />
            );
          }

          return null;
        })}

        {isMobile && (
          <>
            {sendToEmployeeButton && (
              <SendButton>
                <SendDoc
                  type="send-to-employee"
                  id={docHolderId}
                  buttonText={buttonText}
                  disabled={sendToEmployeeButton.disabled}
                  invalidate={() => queryClient.invalidateQueries(entityName)}
                />
              </SendButton>
            )}
            {sendToEmployerButton && (
              <SendButton>
                <SendDoc
                  type="send-to-employer"
                  id={docHolderId}
                  buttonText={buttonText}
                  disabled={sendToEmployerButton.disabled}
                  invalidate={() => queryClient.invalidateQueries(entityName)}
                />
              </SendButton>
            )}
          </>
        )}

        {!isMobile && (
          <>
            <div
              key={`${docHolderId}-common`}
              className="d-inline-block ml-2 mb-2 mb-md-0"
            >
              <TooltipWrapper
                id="tooltip-print"
                overlay={t('documents.table.preview')}
              >
                <Button
                  type="button"
                  btnType="dark"
                  size="sm"
                  outlined
                  disabled={isLoadingDownload}
                  onClick={() => handlePreviewDoc({ showModal, id: documentId, setIsLoading: setIsLoadingDownload })}
                >
                  {!isLoadingDownload && <FaPrint size={14} />}

                  {isLoadingDownload && <Spinner size={14} />}
                </Button>
              </TooltipWrapper>
            </div>
            <div className="d-inline-block ml-2">
              <TooltipWrapper
                id="tooltip-history"
                overlay={t('documents.table.history')}
              >
                <Button
                  type="button"
                  btnType="dark"
                  size="sm"
                  outlined
                  onClick={() => {
                    if (isMultiUser) {
                      history.push(buildClientUrl(`/multiple-document-activity/${documentId}`, client));
                    } else {
                      history.push(buildClientUrl(`/document-activity/${documentId}`, client));
                    }
                  }}
                >
                  <FaHistory size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          </>
        )}
      </>
    );
  };

  const renderMoreButtons = (docHolderId: string, hasLockedDocs?: boolean) => {
    return allDocumentButtons?.map((button: any) => {
      const buttonText = hasLockedDocs ? t('documents.table.docActionsInProgress') : button.text;

      if (button.type === 'move_to_bin' && isMobile)
        return (
          <DeleteDoc
            className="mr-0 ml-0"
            id={docHolderId}
            buttonText={buttonText}
            disabled={button.disabled || hasLockedDocs}
            invalidate={() => queryClient.invalidateQueries(entityName)}
          />
        );

      if (button.type === 'archive' && isMobile)
        return (
          <ArchiveDoc
            className="mr-0 ml-0"
            id={docHolderId}
            entityName={entityName}
            buttonText={buttonText}
            disabled={button.disabled || hasLockedDocs}
            handlePreview={(docId: string) => handlePreviewDoc({ showModal, id: docId })}
            docHolderStatus="archive"
            invalidate={() => queryClient.invalidateQueries(entityName)}
          />
        );

      return null;
    });
  };

  const MobileDocCard: FC<any> = ({ row, allDocumentsData }) => {
    const { id, name, nameEn, buttons, document_holder_id: docHolderId, is_locked: hasLockedDocs, type, number } = row;

    const handleMoreDetails = (modalData: any) => {
      const modalContent = (
        <div className="d-flex flex-column align-items-start">
          <h2 className="text-center mb-3 w-100">{holderName}</h2>
          <p className="mb-1 font-weight-bold">{`${t('documents.table.createdAt')}: ${formatDate(
            new Date(createdAt),
            'dd.mm.YYY hh:ii:ss'
          )}`}</p>
          <p className="mb-1 font-weight-bold">{`${t('documents.table.category')}: ${categoryName}`}</p>

          {/* Repeat this container for each document in the folder */}
          {data.map((doc: any) => (
            <DocumentDetailContainer>
              <p className="mb-2 font-weight-bold">
                #{doc.id} - {doc.name}
              </p>
              <p className="mb-1 font-weight-bold">{t('documents.table.actionsEmployer')}</p>
              <ul className="text-left">
                <li>{doc.statuses.employer_approval_status[0].text}</li>
                <li>{doc.statuses.employer_sign_status[0].text}</li>
              </ul>
              <p className="mb-1 font-weight-bold">{t('documents.table.actionsEmployee')}</p>
              <ul className="text-left">
                <li>{doc.statuses.employee_approval_status[0].text}</li>
                <li>{doc.statuses.employee_sign_status[0].text}</li>
              </ul>

              <div className="d-flex justify-content-around align-items-center w-100">
                <Button
                  type="button"
                  btnType="secondary"
                  className="mr-2"
                  onClick={() => {
                    if (isMultiUser) {
                      history.push(buildClientUrl(`/multiple-document-activity/${id}`, client));
                      hideModal();
                    } else {
                      history.push(buildClientUrl(`/document-activity/${id}`, client));
                      hideModal();
                    }
                  }}
                >
                  {t('documents.table.docActivity')}
                </Button>
                <Button
                  type="button"
                  btnType="primary"
                  className="ml-2"
                  onClick={() => handlePreviewDoc({ showModal, id })}
                >
                  {t('documents.table.previewDoc')}
                </Button>
              </div>
              {buttons.some((button: any) => button.type === 'edit') && (
                <>
                  <Button
                    btnType="primary"
                    className="mt-2 w-100"
                    outlined
                    onClick={() => {
                      // Set current wizard step to 3 before pushing to document wizard view
                      setCurrentStep(3);
                      hideModal();
                      history.push({
                        pathname: `/documents-wizard/step-3/${doc.document_holder_id}`,
                        state: {
                          id: doc.document_holder_id,
                        },
                      });
                    }}
                  >
                    {t('documents.table.editFromFields')}
                  </Button>
                  <Button
                    btnType="primary"
                    className="mt-2 w-100"
                    outlined
                    onClick={() => {
                      // Set current wizard step to 4 before pushing to document wizard view
                      setCurrentStep(4);
                      hideModal();
                      history.push({
                        pathname: `/documents-wizard/step-4/${doc.document_holder_id}`,
                        state: {
                          id: doc.document_holder_id,
                        },
                      });
                    }}
                  >
                    {t('documents.table.editFromText')}
                  </Button>
                </>
              )}
            </DocumentDetailContainer>
          ))}
        </div>
      );

      showModal({
        type: 'info',
        content: modalContent,
      });
    };

    const hasSendToButtons = allDocumentButtons.some(
      (button: any) => button.type === 'send_to_employee' || button.type === 'send_to_employer'
    );

    return (
      <MobileInfoContainer>
        <div
          className="d-flex justify-content-between align-items-center"
          data-doc-number={number}
        >
          <div
            className="info-container-name u-cursor-pointer"
            onClick={() => handlePreviewDoc({ showModal, id })}
          >
            {type === 'internal' && (
              <TooltipWrapper
                id="tooltip-internalDoc"
                overlay={t('documents.internalDoc')}
              >
                {/* <FaFileAlt size={14} /> */}
              </TooltipWrapper>
            )}
            {type === 'external' && (
              <TooltipWrapper
                id="tooltip-externalDoc"
                overlay={t('documents.externalDoc')}
              >
                {/* <FaFileUpload size={14} /> */}
              </TooltipWrapper>
            )}
            <TooltipWrapper
              id="tooltip-docName"
              overlay={language === 'en' ? name : nameEn || name}
              wrapperClass="u-inline"
            >
              <span className="ml-1 u-underline u-color-inherit u-cursor-pointer">
                {language === 'en' ? nameEn || name : name}
              </span>
            </TooltipWrapper>
          </div>
          <div className="info-container-actions">
            <div className="actions-heading">
              {(buttons && buttons.length > 0) || hasSendToButtons
                ? t('documents.table.expectedActions')
                : t('documents.table.noExpectedActions')}
            </div>
            <div className="actions-content">
              {renderActions({ buttons, documentId: id, docHolderId, hasLockedDocs })}
            </div>
          </div>
        </div>
        <div className="mt-2 d-flex justify-content-between">
          {isMobile && <Morebtns>{renderMoreButtons(docHolderId, hasLockedDocs)}</Morebtns>}

          <Button
            btnType="primary"
            outlined
            onClick={() => handleMoreDetails(allDocumentsData)}
          >
            <FaInfoCircle
              size={12}
              color="575962"
              className="mr-2"
            />
            {t('documents.table.moreDetails')}
          </Button>
        </div>
      </MobileInfoContainer>
    );
  };

  return !isMobile ? (
    <ReactTableWrapper<IDocumentGroup>
      data={data}
      columns={buildColumns()}
      totalCount={totalCount}
      hideFilterBar
      className="c-table--expanded"
    />
  ) : (
    <>
      {data.map((row: any, index: any) => {
        return (
          <>
            <MobileDocCard
              key={index}
              row={row}
              allDocumentsData={data}
            />
            <hr className="mb-0 mt-0" />
          </>
        );
      })}
    </>
  );
};

const SendButton = styled.div`
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #55cab2;
  width: 48px;
  height: 48px;
  cursor: pointer;
`;

const Morebtns = styled.div`
  display: flex;
  width: 40%;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
`;

const StatusIconWrapper = styled.div`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;

  &.primary {
    background: #cbeee7;
  }

  &.warning {
    background: #ffeabd;
  }

  &.danger {
    background: #ffcdcd;
  }

  &.gray {
    background: #ddd;
  }
`;

const MobileInfoContainer = styled.div`
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .info-container-name {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 40%;
    height: 100px;
    text-align: center;
    border: 2px solid #cbeee7;
    padding: 0.5rem;
    border-radius: 0.5rem;
    overflow: hidden;
  }

  .info-container-actions {
    width: 60%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem;
    text-align: center;
  }
`;

const DocumentDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: 2px solid #cbeee7;
  width: 100%;
  padding: 1rem;
  margin-top: 1rem;
  border-radius: 1rem;
`;

export default DocsTable;
